/**
 * API Client for EasyPost frontend-backend communication
 */

import type {
  Address,
  AddressRecord,
  ShipmentInput,
  ShipmentPreview,
  ShipmentResult,
  TrackingInfo,
  OrderHistoryEntry,
  OrderHistoryPageResult,
  TrackingSyncResult,
  DateFilters,
  AppSettings,
} from '../types';
import { clearRuntimeApiToken, requireRuntimeApiConfig } from './config';
import type { AddressListRequest, AsyncTaskReceipt } from '../../../shared/api-contracts';
import { resolveLabelContentType } from '../../../shared/label-formats';

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const config = requireRuntimeApiConfig();
  const url = `${config.apiUrl}${endpoint}`;
  const headers = new Headers(options?.headers);
  headers.set('Authorization', `Bearer ${config.token}`);
  if (options?.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorCode = '';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
      errorCode = errorData.code || '';
    } catch {
      // Use default error message
    }
    const authChallenge = response.headers.get('WWW-Authenticate') || '';
    const isWorkerAuthFailure =
      response.status === 401 &&
      (
        authChallenge.toLowerCase().startsWith('bearer') ||
        errorCode === 'app_auth_invalid'
      );
    if (isWorkerAuthFailure) {
      clearRuntimeApiToken(config);
    }
    throw new ApiError(errorMessage, response.status);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

async function requestBlob(endpoint: string, options?: RequestInit): Promise<{
  blob: Blob;
  contentType: string;
}> {
  const config = requireRuntimeApiConfig();
  const headers = new Headers(options?.headers);
  headers.set('Authorization', `Bearer ${config.token}`);
  const response = await fetch(`${config.apiUrl}${endpoint}`, { ...options, headers });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    let code = '';
    try {
      const body = await response.json();
      message = body.error || message;
      code = body.code || '';
    } catch {
      // The status is still useful when the proxy did not return JSON.
    }
    const challenge = response.headers.get('WWW-Authenticate') || '';
    if (
      response.status === 401 &&
      (challenge.toLowerCase().startsWith('bearer') || code === 'app_auth_invalid')
    ) {
      clearRuntimeApiToken(config);
    }
    throw new ApiError(message, response.status);
  }

  return {
    blob: await response.blob(),
    contentType: (response.headers.get('content-type') || '').split(';', 1)[0].trim().toLowerCase(),
  };
}

// Addresses API
export const addressesApi = {
  listPage: async (options: AddressListRequest = {}, signal?: AbortSignal): Promise<{
    addresses: AddressRecord[];
    next_cursor: string | null;
  }> => {
    const params = new URLSearchParams();
    params.set('limit', String(options.limit ?? 200));
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.search) params.set('search', options.search);
    if (options.sort) params.set('sort', options.sort);
    if (options.shipmentFilter) params.set('shipment_filter', options.shipmentFilter);
    const data = await request<{
      addresses: AddressRecord[];
      next_cursor?: string | null;
    }>(`/api/addresses?${params.toString()}`, { signal });
    return { addresses: data.addresses, next_cursor: data.next_cursor ?? null };
  },

  list: async (options: { maxPages?: number; maxAddresses?: number } = {}): Promise<AddressRecord[]> => {
    const maxPages = options.maxPages ?? 25;
    const maxAddresses = options.maxAddresses ?? 5000;
    if (!Number.isSafeInteger(maxPages) || maxPages < 1 || maxPages > 100) {
      throw new Error('maxPages must be an integer between 1 and 100');
    }
    if (!Number.isSafeInteger(maxAddresses) || maxAddresses < 1 || maxAddresses > 20000) {
      throw new Error('maxAddresses must be an integer between 1 and 20000');
    }

    const addresses: AddressRecord[] = [];
    const seenIds = new Set<string>();
    const seenCursors = new Set<string>();
    let cursor: string | undefined;

    for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
      const page = await addressesApi.listPage({ limit: 200, cursor });
      for (const address of page.addresses) {
        if (seenIds.has(address.id)) continue;
        if (addresses.length >= maxAddresses) {
          throw new Error(`Saved-address safety limit reached (${maxAddresses}). Narrow the address book or raise the client cap.`);
        }
        seenIds.add(address.id);
        addresses.push(address);
      }

      if (!page.next_cursor) return addresses;
      if (seenCursors.has(page.next_cursor)) {
        throw new Error('Saved-address pagination returned a repeated cursor.');
      }
      seenCursors.add(page.next_cursor);
      cursor = page.next_cursor;
    }

    throw new Error(`Saved-address page safety limit reached (${maxPages}) before all addresses were loaded.`);
  },

  get: async (id: string): Promise<AddressRecord> => {
    const data = await request<{ address: AddressRecord }>(`/api/addresses/${encodeURIComponent(id)}`);
    return data.address;
  },

  create: async (address: Omit<Address, 'id'>): Promise<AddressRecord> => {
    const data = await request<{ address: AddressRecord }>('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(address),
    });
    return data.address;
  },

  update: async (id: string, address: Partial<Address>): Promise<AddressRecord> => {
    const data = await request<{ address: AddressRecord }>(`/api/addresses/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(address),
    });
    return data.address;
  },

  delete: async (id: string): Promise<void> => {
    await request<void>(`/api/addresses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  markShipped: async (id: string): Promise<{ success: boolean; message: string }> => {
    return request<{ success: boolean; message: string }>(`/api/addresses/${encodeURIComponent(id)}/mark-shipped`, {
      method: 'POST',
    });
  },

  syncFromOrders: async (options: { limit?: number; cursor?: string } = {}): Promise<{
    success: boolean;
    created: number;
    skipped: number;
    total: number;
    processed: number;
    indexed_addresses: number;
    next_cursor: string | null;
    list_complete: boolean;
    phase: 'addresses' | 'orders' | 'complete';
  }> => {
    const params = new URLSearchParams();
    params.set('limit', String(options.limit ?? 100));
    if (options.cursor) params.set('cursor', options.cursor);
    return request<{
      success: boolean;
      created: number;
      skipped: number;
      total: number;
      processed: number;
      indexed_addresses: number;
      next_cursor: string | null;
      list_complete: boolean;
      phase: 'addresses' | 'orders' | 'complete';
    }>(`/api/addresses/sync-from-orders?${params.toString()}`, {
      method: 'POST',
    });
  },

  bulkDelete: async (ids: string[]): Promise<{
    success: boolean;
    deleted: number;
    failed: number;
    error?: string;
  }> => {
    let deleted = 0;
    let failed = 0;
    for (let index = 0; index < ids.length; index += 100) {
      try {
        const result = await request<{ success: boolean; deleted: number; failed: number }>('/api/addresses/bulk-delete', {
          method: 'POST',
          body: JSON.stringify({ ids: ids.slice(index, index + 100) }),
        });
        deleted += result.deleted;
        failed += result.failed;
      } catch (err) {
        // Earlier batches are already committed. Return their known progress
        // so the caller can refresh the authoritative list instead of losing
        // that information in a thrown error.
        return {
          success: false,
          deleted,
          failed: ids.length - deleted,
          error: err instanceof Error ? err.message : 'The delete request was interrupted',
        };
      }
    }
    return { success: failed === 0, deleted, failed };
  },
};

// Shipments API
export const shipmentsApi = {
  preview: async (input: Omit<ShipmentInput, 'confirm'>, signal?: AbortSignal): Promise<ShipmentPreview> => {
    return request<ShipmentPreview>('/shipments', {
      method: 'POST',
      body: JSON.stringify({ ...input, confirm: false }),
      signal,
    });
  },

  confirm: async (input: ShipmentInput, signal?: AbortSignal): Promise<ShipmentResult> => {
    const result = await request<ShipmentResult>('/shipments', {
      method: 'POST',
      body: JSON.stringify({ ...input, confirm: true, confirmed_details: true }),
      signal,
    });
    if (!result || typeof result.shipment_id !== 'string' || !result.shipment_id.trim()) {
      throw new ApiError('The Worker returned an invalid purchase response. Retry the saved prepared shipment.', 502);
    }
    if (input.preview_shipment_id && result.shipment_id !== input.preview_shipment_id) {
      throw new ApiError('The Worker returned a different shipment than the prepared purchase. The retry state was preserved.', 502);
    }
    return result;
  },

  cancel: async (shipmentId: string, recordKey?: string, signal?: AbortSignal): Promise<{
    success: boolean;
    message: string;
    refund_status?: string;
    entry?: OrderHistoryEntry;
  }> => {
    return request<{
      success: boolean;
      message: string;
      refund_status?: string;
      entry?: OrderHistoryEntry;
    }>(`/shipments/${encodeURIComponent(shipmentId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ record_key: recordKey }),
      signal,
    });
  },
};

export const labelsApi = {
  fetch: async (sourceUrl: string, signal?: AbortSignal): Promise<{ blob: Blob; contentType: string }> => {
    let parsed: URL;
    try {
      parsed = new URL(sourceUrl);
    } catch {
      throw new Error('The label source URL is invalid.');
    }
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password) {
      throw new Error('The label source must be a credential-free HTTPS URL.');
    }
    const result = await requestBlob(`/proxy-label?src=${encodeURIComponent(parsed.toString())}`, { signal });
    const prefix = new Uint8Array(await result.blob.slice(0, 8).arrayBuffer());
    const contentType = resolveLabelContentType(result.contentType, prefix, parsed.pathname) || result.contentType;
    // A generic response's Blob retains its original MIME. Correct that too so
    // the PDF iframe/image decoder receives the verified media type.
    return {
      contentType,
      blob: result.blob.type === contentType ? result.blob : new Blob([result.blob], { type: contentType }),
    };
  },
};

// Tracking API
export const trackingApi = {
  getByShipment: async (shipmentId: string): Promise<TrackingInfo> => {
    return request<TrackingInfo>(`/shipments/${encodeURIComponent(shipmentId)}/tracking`);
  },

  syncAll: async (options?: { force?: boolean; limit?: number; cursor?: string }): Promise<TrackingSyncResult | AsyncTaskReceipt> => {
    const params = new URLSearchParams();
    if (options?.force) params.set('force', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.cursor) params.set('cursor', options.cursor);
    const query = params.toString();
    return request<TrackingSyncResult | AsyncTaskReceipt>(`/api/orders/sync-tracking${query ? `?${query}` : ''}`, {
      method: 'POST',
    });
  },

  syncOne: async (shipmentId: string): Promise<{ shipment_id: string; tracking_status: string; tracking_status_label: string; updated: boolean } | AsyncTaskReceipt> => {
    return request<{ shipment_id: string; tracking_status: string; tracking_status_label: string; updated: boolean } | AsyncTaskReceipt>(`/api/orders/${encodeURIComponent(shipmentId)}/sync-tracking`, {
      method: 'POST',
    });
  },
};

// Orders API
export const ordersApi = {
  list: async (
    filters?: DateFilters,
    options: { limit?: number; cursor?: string; signal?: AbortSignal } = {}
  ): Promise<OrderHistoryPageResult> => {
    const params = new URLSearchParams();
    if (filters?.date) params.set('date', filters.date);
    if (filters?.startDate) params.set('start_date', filters.startDate);
    if (filters?.endDate) params.set('end_date', filters.endDate);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status && filters.status !== 'all') params.set('status', filters.status);
    const offsetForDate = (value: string | undefined, nextDay = false) => {
      if (!value) return new Date().getTimezoneOffset();
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (!match) return new Date().getTimezoneOffset();
      const localBoundary = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]) + (nextDay ? 1 : 0),
        0,
        0,
        0,
        0
      );
      return Number.isNaN(localBoundary.getTime())
        ? new Date().getTimezoneOffset()
        : localBoundary.getTimezoneOffset();
    };
    const startBoundary = filters?.date || filters?.startDate;
    const endBoundary = filters?.date || filters?.endDate;
    const startOffset = offsetForDate(startBoundary);
    const endOffset = offsetForDate(endBoundary, true);
    const representativeOffset = startBoundary ? startOffset : endOffset;
    params.set('timezone_offset', String(representativeOffset));
    params.set('start_timezone_offset', String(startOffset));
    params.set('end_timezone_offset', String(endOffset));
    params.set('limit', String(options.limit ?? 200));
    if (options.cursor) params.set('cursor', options.cursor);
    const query = params.toString();
    const data = await request<OrderHistoryPageResult>(`/api/orders${query ? `?${query}` : ''}`, {
      signal: options.signal,
    });
    return {
      orders: data.orders,
      next_cursor: data.next_cursor ?? null,
    };
  },
};

// Settings API
export const settingsApi = {
  get: async (): Promise<AppSettings> => {
    const data = await request<{ settings: AppSettings }>('/api/settings');
    return data.settings;
  },

  update: async (settings: Partial<AppSettings>): Promise<AppSettings> => {
    const data = await request<{ settings: AppSettings }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return data.settings;
  },
};

export { ApiError };
